import Overlay from '@om-fe/core/components/overlay/overlay';
import { EMRoutingContext, RouteNames } from '@om-fe/core/core/routing/utils';
import { AppState } from '@om-fe/core/core/types';
import getConfig, { OMFE2ConfigModel, DateFormatConfigKey, getConfigDateFormatType } from '@om-fe/core/utils/config';
import { CashOutServerType } from '@om-fe/api';
import { OddsFormat } from '@om-fe/utils';
import { BettingHistoryDetails } from '@om-fe/widgets/dist/widgets/betting-history/detail/betting-history-details';
import { BettingHistoryItemV2ClassModifiers } from '@om-fe/widgets/dist/widgets/betting-history/betting-history-item/v2/betting-history-item-v2.utils';
import { BettingHistoryItemLayout } from '@om-fe/widgets/dist/widgets/betting-history/betting-history-item.layout';
import { MatchScoreLayout, MatchScoreScopes } from '@om-fe/widgets/dist/widgets/match-score/match-score.constants';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { compose } from 'redux';
import { BettingHistoryPage } from './betting-history-page';
import { getVirtualState, setVirtualState, VirtualStateType, VirtualStateModel } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';
import { getOpenBetsV2, useLang } from '@om-fe/widgets';
import { useApiConfigHost, useAuthenticationMethodType, useAuthId, useOperatorId, useSessionInfo } from '@om-fe/store';
import { AuthenticationMethodType, BettingHistoryModel, SessionInfoModel } from '@om-fe/models';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

type BettingHistoryDetailsPageRouteProps = RouteComponentProps<{
    readonly betId: string,
}>;

interface BettingHistoryPageStoreProps {
    oddsFormat: OddsFormat,
}

type BettingHistoryPageProps = BettingHistoryDetailsPageRouteProps & BettingHistoryPageStoreProps;

function BettingHistoryDetailsComponent(props: BettingHistoryPageProps) {
    const { getPath, getRouteByUrl } = useContext(EMRoutingContext);
    const config: OMFE2ConfigModel = getConfig();
    const enabledCashoutAble: boolean = config.betslip?.enabledCashoutAbleFilter ?? false;

    const {
        match: { params: { betId } },
        history,
        oddsFormat,
    } = props;

    const lang: string = useLang();
    const host: string | undefined = useApiConfigHost();
    const operatorId: number = useOperatorId();
    const sessionInfo: SessionInfoModel | undefined = useSessionInfo();
    const userId: string | number = sessionInfo?.userIdStr || sessionInfo?.userID || 0;
    const longLiveToken: string | undefined = sessionInfo?.longLiveTokenForBetsAPI;
    const userSessionId: string | undefined = useAuthId();
    const authType: AuthenticationMethodType | undefined = useAuthenticationMethodType();
    const timerId = useRef<number>(0);

    const [openBetsV2, setOpenBetsV2] = useState<BettingHistoryModel[]>([]);
    
    const reFetchHistory = useCallback(async () => {
        if (host && operatorId && userId && userSessionId && authType && lang) {
            dayjs.extend(utc);

            try {
                const response = await getOpenBetsV2({
                    host,
                    operatorId: `${operatorId}`,
                    userId: `${userId}`,
                    userSessionId,
                    authType,
                    limit: '100',
                    placedBefore: dayjs().utc().add(3, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
                    lang,
                    longLiveToken
                });
                if (response.length) {
                    setOpenBetsV2(response);
                }
            } catch (error) {
            }
        }
    }, [host, operatorId, userId, userSessionId, authType, lang, longLiveToken]);

      useEffect(() => {
        if (enabledCashoutAble) {
            void reFetchHistory();
        }

        return () => {
            if (timerId.current) {
                clearTimeout(timerId.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabledCashoutAble]);

    const handleCloseModal = useCallback(() => {
        const virtualState: VirtualStateModel[] = getVirtualState();
        const path: string = getPath(RouteNames.bettingHistory, { page: 1, status: 'settled' });
        if (config.virtualState) {
            const state = setVirtualState(VirtualStateType.forwardState, virtualState, getPath(path), getRouteByUrl(path));
            history.push(path, state);
            setVirtualState(VirtualStateType.newState, virtualState, getPath(path), getRouteByUrl(path));
        } else {
            history.push(path);
        }
    }, [getPath, history]);

    const bet: BettingHistoryModel | undefined = openBetsV2.find(bet => bet.betId === betId);

    return (
        <BettingHistoryPage>
            <Overlay opened={true} onClose={handleCloseModal}>
                <BettingHistoryDetails
                    betId={betId}
                    itemLayout={BettingHistoryItemLayout.V2}
                    classModifier={BettingHistoryItemV2ClassModifiers.Big}
                    oddsFormat={oddsFormat}
                    matchScoreScope={MatchScoreScopes.BHDetailed}
                    matchScoreLayout={MatchScoreLayout.V1}
                    statisticsExpandedByDefault={true}
                    dateFormat={getConfigDateFormatType(DateFormatConfigKey.FormatFullDate)}
                    cashoutServer={CashOutServerType.v2}
                    enabledCashoutAble={enabledCashoutAble}
                    bet={bet}
                />
                <GeneralIcon name="close" onClick={handleCloseModal} />
            </Overlay>
        </BettingHistoryPage>
    );
}

export const BettingHistoryDetailsPage = compose(
    withRouter,
    connect<BettingHistoryPageStoreProps, null, BettingHistoryPageProps, AppState>(
        (state: AppState): BettingHistoryPageStoreProps => {
            return {
                oddsFormat: state.settings.oddsFormat,
            };
        },
    ),
)(BettingHistoryDetailsComponent);
